import { useMutation, useQueryClient } from 'react-query';

import useFetchAgreements from './useFetchAgreements';
import { useFetchUserAcceptedAgreements } from 'components/views/user/general/user_agreements/hooks';
import { postAgreements } from 'components/views/user/general/user_agreements/helpers';
import { patchUpdatePhoneNumber } from './helpers';

import {
	AGREEMENTS_TYPES,
	QUERY_LOYALTY_AGREEMENTS,
	QUERY_SUBSCRIBER_ACTIVE_AGREEMENTS,
	QUERY_SUBSCRIBER_DETAILS
} from 'helpers';

const { LOYALTY } = AGREEMENTS_TYPES;

const useKinometryConsent = () => {
	const queryClient = useQueryClient();

	const { agreements, isAgreementsLoading, isAgreementsError } =
		useFetchAgreements({
			queryKey: QUERY_LOYALTY_AGREEMENTS,
			agreementsToFetch: [LOYALTY]
		});

	const {
		userActiveAgreements,
		isUserActiveAgreementsLoading,
		isUserActiveAgreementsError
	} = useFetchUserAcceptedAgreements();

	const loyaltyAgreement = agreements[0];
	const loyaltyAgreementVersionId = loyaltyAgreement?.version?.id;

	const isAccepted =
		loyaltyAgreementVersionId &&
		userActiveAgreements.some(
			({ agreement_version }) =>
				agreement_version.id === loyaltyAgreementVersionId
		);

	const invalidateKinometryConsentQueries = async () => {
		await Promise.all([
			queryClient.invalidateQueries(QUERY_SUBSCRIBER_ACTIVE_AGREEMENTS),
			queryClient.invalidateQueries(QUERY_SUBSCRIBER_DETAILS)
		]);
	};

	const {
		mutate: toggleLoyaltyAgreement,
		isLoading: isToggleLoading,
		isError: isToggleError
	} = useMutation({
		mutationFn: async (shouldAccept) => {
			if (!loyaltyAgreementVersionId) {
				return;
			}

			if (shouldAccept) {
				await postAgreements([loyaltyAgreementVersionId], true);
				return;
			}

			await postAgreements([loyaltyAgreementVersionId], false);
			await patchUpdatePhoneNumber(null);
		},
		onSuccess: invalidateKinometryConsentQueries
	});

	const handleToggleAgreement = () => {
		if (!loyaltyAgreementVersionId || isToggleLoading) {
			return;
		}

		toggleLoyaltyAgreement(!isAccepted);
	};

	const isLoading = isAgreementsLoading || isUserActiveAgreementsLoading;

	const isError =
		isAgreementsError || isUserActiveAgreementsError || isToggleError;

	return {
		loyaltyAgreement,
		isAccepted,
		isLoading,
		isError,
		isToggleLoading,
		handleToggleAgreement
	};
};

export default useKinometryConsent;
